/**
 * Thin LiFi REST client (li.quest) — one integration surface for all three
 * live legs: bridge (Gnosis→4663), destination swap (4663 same-chain) and
 * gas top-up (Gnosis→ETH on 4663). No SDK dependency; the API is stable and
 * was verified with live quotes on 2026-08-10 (docs/RESEARCH.md).
 */
import {
  erc20Abi,
  keccak256,
  type Account,
  type LocalAccount,
  type PublicClient,
  type TransactionSerializable,
} from "viem";
import { LIFI } from "./config";
import { gnosisPublicClient, robinhoodPublicClient, walletClientFor } from "./chains";
import type { Hex } from "./types";

export interface LifiQuote {
  tool: string;
  fromChainId: number;
  toChainId: number;
  fromToken: Hex;
  fromAmount: bigint;
  toAmount: bigint;
  toAmountMin: bigint;
  /** Sum of LiFi fee-cost estimates, USD e6 (informational). */
  feeUsdE6: number;
  approvalAddress: Hex | null;
  txRequest: { to: Hex; data: Hex; value: bigint; chainId: number };
}

const NATIVE = "0x0000000000000000000000000000000000000000";

export class LifiError extends Error {
  constructor(
    message: string,
    readonly kind: "amount_too_low" | "no_route" | "http" | "failed",
  ) {
    super(message);
  }
}

export async function getLifiQuote(params: {
  fromChain: number;
  toChain: number;
  fromToken: Hex | "native";
  toToken: Hex | "native";
  fromAmount: bigint;
  fromAddress: Hex;
  slippage: number;
}): Promise<LifiQuote> {
  const q = new URLSearchParams({
    fromChain: String(params.fromChain),
    toChain: String(params.toChain),
    fromToken: params.fromToken === "native" ? NATIVE : params.fromToken,
    toToken: params.toToken === "native" ? NATIVE : params.toToken,
    fromAmount: params.fromAmount.toString(),
    fromAddress: params.fromAddress,
    slippage: String(params.slippage),
  });
  if (LIFI.integrator) q.set("integrator", LIFI.integrator);

  const res = await fetch(`${LIFI.apiBase}/quote?${q}`, { signal: AbortSignal.timeout(20_000) });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    const lower = body.toLowerCase();
    if (lower.includes("too low") || lower.includes("minamount")) {
      throw new LifiError(`Amount below route minimum: ${body.slice(0, 200)}`, "amount_too_low");
    }
    if (res.status === 404) throw new LifiError(`No route: ${body.slice(0, 200)}`, "no_route");
    throw new LifiError(`LiFi quote HTTP ${res.status}: ${body.slice(0, 200)}`, "http");
  }
  const d = await res.json();
  const e = d.estimate;
  const feeUsd = (e.feeCosts ?? []).reduce((s: number, f: { amountUSD?: string }) => s + Number(f.amountUSD ?? 0), 0);
  return {
    tool: d.tool,
    fromChainId: params.fromChain,
    toChainId: params.toChain,
    fromToken: params.fromToken === "native" ? (NATIVE as Hex) : params.fromToken,
    fromAmount: BigInt(e.fromAmount),
    toAmount: BigInt(e.toAmount),
    toAmountMin: BigInt(e.toAmountMin),
    feeUsdE6: Math.round(feeUsd * 1e6),
    approvalAddress: (e.approvalAddress ?? null) as Hex | null,
    txRequest: {
      to: d.transactionRequest.to as Hex,
      data: d.transactionRequest.data as Hex,
      value: BigInt(d.transactionRequest.value ?? 0),
      chainId: Number(d.transactionRequest.chainId),
    },
  };
}

function publicClientFor(chainId: number): PublicClient {
  return chainId === 100 ? gnosisPublicClient() : (robinhoodPublicClient() as PublicClient);
}

/**
 * Approve (exact amount, BRIEF §35: minimal allowances) if needed, then send
 * the quote's transaction. Returns the submitted tx hash immediately so the
 * caller can persist it BEFORE waiting — that's what makes crash recovery
 * possible.
 */
async function ensureLifiAllowance(account: Account, quote: LifiQuote): Promise<void> {
  const publicClient = publicClientFor(quote.fromChainId);
  const wallet = walletClientFor(quote.fromChainId, account);

  if (quote.approvalAddress && quote.fromToken !== NATIVE) {
    const allowance = await publicClient.readContract({
      address: quote.fromToken,
      abi: erc20Abi,
      functionName: "allowance",
      args: [account.address, quote.approvalAddress],
    });
    if (allowance < quote.fromAmount) {
      const approveHash = await wallet.writeContract({
        address: quote.fromToken,
        abi: erc20Abi,
        functionName: "approve",
        args: [quote.approvalAddress, quote.fromAmount],
        chain: wallet.chain,
        account,
      });
      await publicClient.waitForTransactionReceipt({ hash: approveHash, timeout: 120_000 });
    }
  }
}

export async function sendLifiTransaction(account: Account, quote: LifiQuote): Promise<Hex> {
  await ensureLifiAllowance(account, quote);
  const wallet = walletClientFor(quote.fromChainId, account);

  return wallet.sendTransaction({
    to: quote.txRequest.to,
    data: quote.txRequest.data,
    value: quote.txRequest.value,
    chain: wallet.chain,
    account,
  });
}

export interface PreparedLifiTransaction {
  hash: Hex;
  serializedTransaction: Hex;
}

function rawTransactionAlreadyKnown(error: unknown): boolean {
  const message = String(error instanceof Error ? error.message : error).toLowerCase();
  return (
    message.includes("already known") ||
    message.includes("known transaction") ||
    message.includes("nonce too low")
  );
}

/**
 * Sign a LiFi transaction, let the caller durably record those exact bytes,
 * then broadcast it. A restart can rebroadcast the same bytes without ever
 * spending the input twice.
 */
export async function sendRecoverableLifiTransaction(
  account: LocalAccount,
  quote: LifiQuote,
  onPrepared: (transaction: PreparedLifiTransaction) => Promise<void> | void,
): Promise<Hex> {
  await ensureLifiAllowance(account, quote);
  const publicClient = publicClientFor(quote.fromChainId);
  const wallet = walletClientFor(quote.fromChainId, account);
  const request = await wallet.prepareTransactionRequest({
    to: quote.txRequest.to,
    data: quote.txRequest.data,
    value: quote.txRequest.value,
    chain: wallet.chain,
    account,
  });
  const serializedTransaction = await account.signTransaction(request as TransactionSerializable);
  const hash = keccak256(serializedTransaction);
  await onPrepared({ hash, serializedTransaction });
  try {
    await publicClient.sendRawTransaction({ serializedTransaction });
  } catch (error) {
    if (!rawTransactionAlreadyKnown(error)) throw error;
  }
  return hash;
}

export interface LifiStatusResult {
  status: "DONE" | "PENDING" | "FAILED" | "INVALID" | "NOT_FOUND";
  /** Amount received on the destination, base units, when DONE. */
  receivedAmount?: bigint;
  receivedTxHash?: Hex;
}

export async function getLifiStatus(txHash: Hex, fromChain: number, toChain: number): Promise<LifiStatusResult> {
  const q = new URLSearchParams({
    txHash,
    fromChain: String(fromChain),
    toChain: String(toChain),
  });
  const res = await fetch(`${LIFI.apiBase}/status?${q}`, { signal: AbortSignal.timeout(15_000) });
  if (!res.ok) return { status: "NOT_FOUND" };
  const d = await res.json();
  const status = (d.status ?? "PENDING") as LifiStatusResult["status"];
  return {
    status,
    receivedAmount: d.receiving?.amount ? BigInt(d.receiving.amount) : undefined,
    receivedTxHash: d.receiving?.txHash as Hex | undefined,
  };
}

/** Poll until DONE/FAILED or timeout (stuck legs stay recoverable, not lost). */
export async function waitLifiDone(txHash: Hex, fromChain: number, toChain: number): Promise<LifiStatusResult> {
  const deadline = Date.now() + LIFI.statusTimeoutMs;
  for (;;) {
    const s = await getLifiStatus(txHash, fromChain, toChain);
    if (s.status === "DONE") return s;
    if (s.status === "FAILED" || s.status === "INVALID") {
      throw new LifiError(`LiFi leg ${s.status.toLowerCase()} (${txHash})`, "failed");
    }
    if (Date.now() > deadline) throw new LifiError(`LiFi leg timed out (${txHash})`, "failed");
    await new Promise((r) => setTimeout(r, LIFI.statusPollMs));
  }
}
